import React, { useState } from "react";
import {
  Card,
  Box,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

const initialData = [
  { name: "Regular Frame", dataBytes: "", framesPerDay: "", numDays: "" },
  { name: "HUPS Data", dataBytes: "", framesPerDay: "", numDays: "" },
  { name: "Image", dataBytes: "", framesPerDay: "", numDays: "" },
];

const DataCalculator = () => {
  const [rows, setRows] = useState(initialData);
  const [numSites, setNumSites] = useState("");

  // Handle input changes
  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
  };

  const totalGBPerMonth = rows.reduce(
    (total, row) =>
      total +
      (row.dataBytes && row.framesPerDay && row.numDays
        ? (Number(row.dataBytes) *
            Number(row.framesPerDay) *
            Number(row.numDays)) /
          1073741824
        : 0),
    0
  );

  // Convert GB to TB
  const totalStorageGB = totalGBPerMonth * numSites;
  const totalStorageTB = totalStorageGB / 1024;

  return (
    <Box m="1.5rem 2.5rem">
      <div className="container-fluid page-body-wrapper">
        <Card elevation={4} sx={{ borderRadius: "12px", overflow: "hidden" }}>
          <CardContent>
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                fontWeight: "bold",
                fontSize: "30px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              Data Calculator
            </Typography>
            <TableContainer
              component={Paper}
              sx={{ borderRadius: "12px", overflow: "hidden" }}
            >
              <Table>
                {/* Table Header */}
                <TableHead>
                  <TableRow sx={{ backgroundColor: "rgb(250,223,186)" }}>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: "15px",
                        color: "#997d3d",
                      }}
                    >
                      Frame Type
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: "15px",
                        color: "#997d3d",
                      }}
                    >
                      Data Bytes/Frame
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: "15px",
                        color: "#997d3d",
                      }}
                    >
                      Frames/Day
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: "15px",
                        color: "#997d3d",
                      }}
                    >
                      Number of Days
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        fontSize: "15px",
                        color: "#997d3d",
                      }}
                    >
                      Database Required (GB)
                    </TableCell>
                  </TableRow>
                </TableHead>

                {/* Table Body */}
                <TableBody>
                  {rows.map((row, index) => {
                    const databaseRequired =
                      row.dataBytes && row.framesPerDay && row.numDays
                        ? (Number(row.dataBytes) *
                            Number(row.framesPerDay) *
                            Number(row.numDays)) /
                          1073741824
                        : 0;

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            color: "#997d3d",
                            fontSize: "15px",
                          }}
                        >
                          {row.name}
                        </TableCell>

                        <TableCell>
                          <TextField
                            type="number"
                            value={row.dataBytes}
                            onChange={(e) =>
                              handleChange(index, "dataBytes", e.target.value)
                            }
                            variant="outlined"
                            size="small"
                            sx={{ width: "100px" }}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            type="number"
                            value={row.framesPerDay}
                            onChange={(e) =>
                              handleChange(
                                index,
                                "framesPerDay",
                                e.target.value
                              )
                            }
                            variant="outlined"
                            size="small"
                            sx={{ width: "100px" }}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            type="number"
                            value={row.numDays}
                            onChange={(e) =>
                              handleChange(index, "numDays", e.target.value)
                            }
                            variant="outlined"
                            size="small"
                            sx={{ width: "100px" }}
                          />
                        </TableCell>

                        <TableCell
                          sx={{ fontWeight: "bold", fontSize: "15px" }}
                        >
                          {databaseRequired} GB
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* Total Row */}
                  <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                    <TableCell
                      colSpan={4}
                      sx={{
                        fontWeight: "bold",
                        color: "#997d3d",
                        textAlign: "right",
                        fontSize: "15px",
                      }}
                    >
                      Total Database Required (GB):
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "15px" }}>
                      {totalGBPerMonth.toFixed(2)} GB
                    </TableCell>
                  </TableRow>

                  {/* Number of Sites Input */}
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{
                        fontWeight: "bold",
                        color: "#997d3d",
                        textAlign: "right",
                        fontSize: "15px",
                      }}
                    >
                      Total Number of Sites:
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      sx={{ fontWeight: "bold", textAlign: "left" }}
                    >
                      <TextField
                        type="number"
                        label="Number of Sites"
                        value={numSites}
                        onChange={(e) =>
                          setNumSites(
                            e.target.value === "" ? "" : Number(e.target.value)
                          )
                        }
                        variant="outlined"
                        size="small"
                        sx={{ width: "150px" }}
                      />
                    </TableCell>
                  </TableRow>

                  {/* Total Storage in TB */}
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      sx={{
                        fontWeight: "bold",
                        color: "#997d3d",
                        textAlign: "right",
                        fontSize: "15px",
                      }}
                    >
                      Total Storage Required:
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      sx={{ fontWeight: "bold", fontSize: "15px" }}
                    >
                      {totalStorageGB.toFixed(2)} GB /{" "}
                      {totalStorageTB.toFixed(2)} TB
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </div>
    </Box>
  );
};

export default DataCalculator;
